import React from "react";
import { Paper, Stack, Typography, Box, Skeleton } from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import InfoButton from "../Clinical/InfoButton";
import CMStatCards from "./CMStatCards";
import ERTable from "./ERTable";

/**
 * Panel ER & Utilization — muestra estadísticas de ER frequent flyers
 * y una tabla con los pacientes con mayor uso de emergencias.
 *
 * @param {{
 *   data: { stats: { frequentFlyers, totalPreventableER, bhRelatedER }, topFlyers: Array },
 *   loading: boolean,
 *   onCreateTask: (row) => void,
 *   helpContent: { title, description, sections }
 * }} props
 */
export default function PanelERUtilization({ data, loading, onCreateTask, helpContent }) {
  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <Skeleton variant="rectangular" animation="wave" height={300} sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  const stats = data?.stats;
  const flyers = data?.topFlyers;
  const hasFlyers = Array.isArray(flyers) && flyers.length > 0;

  // ── Stat card items ────────────────────────────────────────────
  const statItems = [
    {
      label: "ER Frequent Flyers",
      value: stats?.frequentFlyers,
      color: "#DC2626",
      bgColor: "#FEE2E2",
    },
    {
      label: "Preventable ER",
      value: stats?.totalPreventableER,
      color: "#D97706",
      bgColor: "#FEF3C7",
    },
    {
      label: "BH-Related ER",
      value: stats?.bhRelatedER,
      color: "#4F46E5",
      bgColor: "#E0E7FF",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: "#FEE2E2",
              color: "#DC2626",
            }}
          >
            <LocalHospitalIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={700}>
            ER &amp; Utilization
          </Typography>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Stat Cards */}
        <CMStatCards items={statItems} />

        {/* Table or empty state */}
        {hasFlyers ? (
          <ERTable flyers={flyers} onCreateTask={onCreateTask} />
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}
          >
            No frequent ER flyers
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
